/**
 * Horizontaler Slider: Kind-Container werden zu Slides (je 100 % Breite),
 * mit CSS Scroll Snap X, Wheel-Übersetzung, Maus-Drag und Navigation.
 *
 * Am Anfang/Ende lässt der Slider Wheel-Events an die vertikale Engine
 * durchreichen (Boundary-Handoff).
 */

import { Navigation } from './Navigation.js';
import { clamp, dispatch, formatLabel, normalizeWheelDelta } from './utils.js';

const GESTURE_GAP = 80;
const THRESHOLD = 60;
const DRAG_MIN = 5;

export class HorizontalSlider {
	constructor( el, globalConfig ) {
		this.el = el;
		this.i18n = globalConfig.i18n || {};

		let config = {};

		try {
			config = JSON.parse( el.dataset.sfpsH || '{}' );
		} catch ( e ) {
			config = {};
		}

		this.config = { arrows: true, dots: true, loop: false, ...config };

		const inner = el.querySelector( ':scope > .e-con-inner' );
		this.scroller = inner || el;
		this.slides = Array.from( this.scroller.children ).filter( ( child ) =>
			child.matches( '.e-con, .elementor-element' )
		);

		this.index = 0;
		this.locked = false;
		this.acc = 0;
		this.lastWheelTime = 0;
		this.gestureConsumed = false;
		this.settleTimer = 0;
		this.nav = null;
		this.drag = null;

		if ( this.slides.length < 2 ) {
			return;
		}

		this.el.classList.add( 'sfps-h-ready' );
		this.scroller.classList.add( 'sfps-h-scroller' );

		this.slides.forEach( ( slide, i ) => {
			slide.classList.add( 'sfps-slide' );

			if ( ! slide.hasAttribute( 'aria-label' ) ) {
				slide.setAttribute( 'aria-label', formatLabel( this.i18n.slide, i + 1 ) );
			}

			slide.setAttribute( 'role', 'group' );
		} );

		this.buildNavigation();
		this.bindEvents();
	}

	buildNavigation() {
		if ( ! this.config.dots && ! this.config.arrows ) {
			return;
		}

		this.nav = new Navigation( this.el, {
			axis: 'h',
			count: this.slides.length,
			dots: this.config.dots,
			arrows: this.config.arrows,
			loop: this.config.loop,
			navLabel: this.i18n.sliderNav || 'Slides',
			itemLabel: this.i18n.slide,
			prevLabel: this.i18n.prevSlide || 'Previous slide',
			nextLabel: this.i18n.nextSlide || 'Next slide',
			labels: null,
			onSelect: ( index ) => this.goTo( index, { source: 'nav' } ),
			onStep: ( direction ) => this.step( direction, 'nav' ),
		} );

		this.nav.setActive( 0 );
	}

	bindEvents() {
		this.onWheel = this.onWheel.bind( this );
		this.onScroll = this.onScroll.bind( this );
		this.onPointerDown = this.onPointerDown.bind( this );
		this.onPointerMove = this.onPointerMove.bind( this );
		this.onPointerUp = this.onPointerUp.bind( this );
		this.suppressClick = this.suppressClick.bind( this );

		this.el.addEventListener( 'wheel', this.onWheel, { passive: false } );
		this.scroller.addEventListener( 'scroll', this.onScroll, { passive: true } );
		this.scroller.addEventListener( 'pointerdown', this.onPointerDown );
	}

	/* ------------------------------------------------------------------ *
	 * Wheel → horizontale Navigation mit Boundary-Handoff
	 * ------------------------------------------------------------------ */

	onWheel( event ) {
		// Echte horizontale Trackpad-Gesten scrollt der Browser nativ.
		if ( Math.abs( event.deltaX ) > Math.abs( event.deltaY ) ) {
			event.stopPropagation();
			return;
		}

		const delta = normalizeWheelDelta( event );
		const direction = delta > 0 ? 1 : -1;
		const now = performance.now();
		const isNewGesture = now - this.lastWheelTime > GESTURE_GAP;
		this.lastWheelTime = now;

		if ( isNewGesture ) {
			this.gestureConsumed = false;
			this.acc = 0;
		}

		// Momentum einer bereits verarbeiteten Geste verschlucken,
		// damit es nicht in die vertikale Engine durchsickert.
		if ( this.gestureConsumed || this.locked ) {
			this.gestureConsumed = true;
			event.preventDefault();
			event.stopPropagation();
			return;
		}

		const atEdge =
			! this.config.loop &&
			( ( direction > 0 && this.index >= this.slides.length - 1 ) ||
				( direction < 0 && this.index <= 0 ) );

		if ( atEdge ) {
			// An die vertikale Engine durchreichen.
			return;
		}

		event.preventDefault();
		event.stopPropagation();

		this.acc += delta;

		if ( Math.abs( this.acc ) >= THRESHOLD ) {
			this.acc = 0;
			this.gestureConsumed = true;
			this.step( direction, 'wheel' );
		}
	}

	/* ------------------------------------------------------------------ *
	 * Maus-Drag
	 * ------------------------------------------------------------------ */

	onPointerDown( event ) {
		if ( event.pointerType !== 'mouse' || event.button !== 0 ) {
			return;
		}

		this.drag = {
			startX: event.clientX,
			startScroll: this.scroller.scrollLeft,
			moved: false,
		};

		window.addEventListener( 'pointermove', this.onPointerMove );
		window.addEventListener( 'pointerup', this.onPointerUp );
	}

	onPointerMove( event ) {
		if ( ! this.drag ) {
			return;
		}

		const deltaX = event.clientX - this.drag.startX;

		if ( ! this.drag.moved && Math.abs( deltaX ) > DRAG_MIN ) {
			this.drag.moved = true;
			this.scroller.classList.add( 'sfps-dragging' );
			this.el.addEventListener( 'click', this.suppressClick, true );
		}

		if ( this.drag.moved ) {
			this.scroller.scrollLeft = this.drag.startScroll - deltaX;
		}
	}

	onPointerUp() {
		window.removeEventListener( 'pointermove', this.onPointerMove );
		window.removeEventListener( 'pointerup', this.onPointerUp );

		if ( ! this.drag ) {
			return;
		}

		const moved = this.drag.moved;
		this.drag = null;

		if ( moved ) {
			this.scroller.classList.remove( 'sfps-dragging' );
			setTimeout( () => this.el.removeEventListener( 'click', this.suppressClick, true ), 0 );

			// Auf die nächstgelegene Slide einrasten.
			const nearest = Math.round( this.scroller.scrollLeft / this.slideWidth() );
			this.goTo( clamp( nearest, 0, this.slides.length - 1 ), { source: 'drag' } );
		}
	}

	suppressClick( event ) {
		event.stopPropagation();
		event.preventDefault();
	}

	/* ------------------------------------------------------------------ *
	 * Navigation
	 * ------------------------------------------------------------------ */

	slideWidth() {
		return this.scroller.clientWidth || 1;
	}

	step( direction, source ) {
		if ( this.locked ) {
			return true;
		}

		let target = this.index + direction;

		if ( this.config.loop ) {
			target = ( target + this.slides.length ) % this.slides.length;
		} else if ( target < 0 || target >= this.slides.length ) {
			return false;
		}

		return this.goTo( target, { source } );
	}

	goTo( index, options = {} ) {
		const opts = { source: 'api', instant: false, ...options };
		index = clamp( index, 0, this.slides.length - 1 );

		if ( index === this.index && opts.source !== 'drag' ) {
			return false;
		}

		const from = this.index;

		if (
			! dispatch(
				this.el,
				'sfps:beforeSlide',
				{ from, to: index, slider: this.el, source: opts.source },
				true
			)
		) {
			return false;
		}

		this.locked = true;
		this.index = index;

		const targetLeft =
			this.slides[ index ].getBoundingClientRect().left -
			this.scroller.getBoundingClientRect().left +
			this.scroller.scrollLeft;

		this.scroller.scrollTo( {
			left: targetLeft,
			behavior: opts.instant ? 'auto' : 'smooth',
		} );

		this.updateNav();
		this.awaitSettle( () => {
			this.locked = false;
			dispatch( this.el, 'sfps:afterSlide', {
				from,
				to: this.index,
				slider: this.el,
				source: opts.source,
			} );
		} );

		return true;
	}

	awaitSettle( done ) {
		let settled = false;
		let fallback = 0;

		const finish = () => {
			if ( settled ) {
				return;
			}

			settled = true;
			clearTimeout( fallback );
			this.scroller.removeEventListener( 'scrollend', finish );
			done();
		};

		if ( 'onscrollend' in window ) {
			this.scroller.addEventListener( 'scrollend', finish, { once: true } );
			fallback = setTimeout( finish, 1200 );
		} else {
			fallback = setTimeout( finish, 700 );
		}
	}

	/**
	 * Nativen Scroll (Touch, Trackpad-X) beobachten und Zustand nachführen.
	 */
	onScroll() {
		if ( this.locked ) {
			return;
		}

		clearTimeout( this.settleTimer );
		this.settleTimer = setTimeout( () => {
			const nearest = clamp(
				Math.round( this.scroller.scrollLeft / this.slideWidth() ),
				0,
				this.slides.length - 1
			);

			if ( nearest === this.index ) {
				return;
			}

			const from = this.index;
			this.index = nearest;
			this.updateNav();

			dispatch( this.el, 'sfps:afterSlide', {
				from,
				to: nearest,
				slider: this.el,
				source: 'scroll',
			} );
		}, 120 );
	}

	updateNav() {
		if ( this.nav ) {
			this.nav.setActive( this.index );
		}
	}

	destroy() {
		this.el.removeEventListener( 'wheel', this.onWheel );
		this.scroller.removeEventListener( 'scroll', this.onScroll );
		this.scroller.removeEventListener( 'pointerdown', this.onPointerDown );
		window.removeEventListener( 'pointermove', this.onPointerMove );
		window.removeEventListener( 'pointerup', this.onPointerUp );
		clearTimeout( this.settleTimer );

		if ( this.nav ) {
			this.nav.destroy();
			this.nav = null;
		}

		this.el.classList.remove( 'sfps-h-ready' );
		this.scroller.classList.remove( 'sfps-h-scroller', 'sfps-dragging' );
		this.slides.forEach( ( slide ) => slide.classList.remove( 'sfps-slide' ) );
	}
}
